import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Bell, Zap, LogIn, HelpCircle, Menu, X, LogOut,
  UserCircle, Settings, Camera, Server, Users, Search, Activity,
  Shield, Globe, Terminal, ChevronRight, Hash, Cpu, Car, UserCheck,
  Users2, Footprints, ParkingSquare, MapPin
} from 'lucide-react';
import logo from '../assets/logo.png';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import NotificationBell from './NotificationBell';
import { motion, AnimatePresence } from 'framer-motion';

interface CSENSELayoutProps {
  children: React.ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
  isAuthenticated?: boolean;
  user?: any;
  onShowAuth?: () => void;
  onShowSupport?: () => void;
  onLogout?: () => void;
  alertCount?: number;
  notifications?: any[];
  onMarkNotificationAsRead?: (id: string) => void;
  onMarkAllNotificationsAsRead?: () => void;
  onDeleteNotification?: (id: string) => void;
  showAgentOverlay?: boolean;
}

export function CSENSELayout({
  children,
  activeTab,
  onTabChange,
  isAuthenticated = false,
  user,
  onShowAuth,
  onLogout,
  alertCount = 0
}: CSENSELayoutProps) {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isSuperAdmin = !!user?.is_super_admin;

  const menuItems = [
    { title: "Dashboard", url: "dashboard", icon: LayoutDashboard, sub: "Overview" },
    { title: "Cameras", url: "cameras", icon: Camera, sub: "Live Monitoring" },
    { title: "AI Intelligence", url: "history", icon: Activity, sub: "Detection Events" },
    { title: "Vehicle Detection", url: "vehicles", icon: Car, sub: "ANPR Log" },
    { title: "AI Attendance", url: "attendance", icon: UserCheck, sub: "Entry / Exit" },
    { title: "People Count", url: "people-count", icon: Users2, sub: "Density Events" },
    { title: "Footfall", url: "footfall", icon: Footprints, sub: "Visitor Traffic" },
    { title: "Parking Management", url: "parking", icon: ParkingSquare, sub: "Zone Occupancy" },
    { title: "Locations", url: "locations", icon: MapPin, sub: "Sites & Zones" },
    { title: "Devices", url: "edge-devices", icon: Server, sub: "Edge Fleet" },
    ...(isSuperAdmin ? [{ title: "Strategic CRM", url: "crm", icon: Users, sub: "Client Accounts" }] : []),
    { title: "Alerts", url: "alerts", icon: Bell, sub: "Notification Queue", count: alertCount }
  ];

  const bottomItems = [
    { title: "Settings", url: "settings", icon: Settings },
    { title: "Profile", url: "profile", icon: UserCircle }
  ];

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white selection:bg-blue-500/30">
      {/* Structural Backgrounds */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/[0.03] blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-600/[0.03] blur-[120px]" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.02] mix-blend-overlay" />
      </div>

      {/* Sidebar - Desktop Only */}
      <aside 
        onMouseEnter={() => setIsSidebarExpanded(true)}
        onMouseLeave={() => setIsSidebarExpanded(false)}
        className={`fixed left-0 top-0 bottom-0 z-50 hidden md:flex flex-col bg-[#0d0f17]/40 backdrop-blur-3xl border-r border-white/5 transition-all duration-500 ease-in-out group ${isSidebarExpanded ? 'w-72' : 'w-20'}`}
      >
        {/* Sidebar Header/Logo */}
        <div className="h-24 flex items-center px-6 border-b border-white/5 overflow-hidden whitespace-nowrap">
           <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(37,99,235,0.1)]">
                 <img src={logo} alt="C" className="w-6 h-6 object-contain" />
              </div>
              <motion.div 
                animate={{ opacity: isSidebarExpanded ? 1 : 0, x: isSidebarExpanded ? 0 : -10 }}
                className="flex flex-col"
              >
                 <span className="text-sm font-black tracking-tighter uppercase text-white">3RDI</span>
                 <span className="text-[9px] font-black tracking-[0.2em] uppercase text-blue-400">Vision Intelligence</span>
              </motion.div>
           </div>
        </div>

        {/* Navigation Section */}
        <nav className="flex-1 py-8 px-4 space-y-2 overflow-y-auto overflow-x-hidden no-scrollbar">
           {menuItems.map((item) => (
             <button
               key={item.url}
               onClick={() => onTabChange(item.url)}
               className={`w-full flex items-center gap-4 p-3 rounded-2xl transition-all relative group/item ${isSidebarExpanded ? '' : 'justify-center'} ${activeTab === item.url ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20 shadow-[0_0_20px_rgba(37,99,235,0.05)]' : 'text-blue-100/40 hover:bg-white/5 hover:text-white border border-transparent'}`}
             >
                <item.icon className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${activeTab === item.url ? 'scale-110' : 'group-hover/item:scale-110'}`} />
                <AnimatePresence initial={false}>
                  {isSidebarExpanded && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.15 }}
                      className="flex flex-col items-start overflow-hidden whitespace-nowrap"
                    >
                       <span className="text-xs font-black uppercase tracking-tight">{item.title}</span>
                       <span className="text-[9px] font-medium opacity-40">{item.sub}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                {item.count ? item.count > 0 && (
                   <span className={`absolute ${isSidebarExpanded ? 'right-4' : 'right-1.5 top-1.5'} min-w-[18px] h-[18px] bg-red-600 text-[10px] flex items-center justify-center rounded-full font-black border-2 border-[#0d0f17]`}>
                      {item.count}
                   </span>
                ) : null}
                {activeTab === item.url && (
                  <motion.div layoutId="sidebar-active" className="absolute inset-0 bg-blue-500/5 rounded-2xl -z-10" />
                )}
             </button>
           ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-white/5 space-y-2">
           {bottomItems.map((item) => (
             <button
               key={item.url}
               onClick={() => onTabChange(item.url)}
               className={`w-full flex items-center gap-4 p-3 rounded-2xl transition-all ${isSidebarExpanded ? '' : 'justify-center'} ${activeTab === item.url ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20' : 'text-blue-100/40 hover:bg-white/5 hover:text-white'}`}
             >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <AnimatePresence initial={false}>
                  {isSidebarExpanded && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.15 }}
                      className="text-xs font-black uppercase tracking-tight whitespace-nowrap overflow-hidden"
                    >
                       {item.title}
                    </motion.span>
                  )}
                </AnimatePresence>
             </button>
           ))}
           <div className="pt-4">
              <div className={`bg-white/5 border border-white/5 rounded-3xl p-2 flex items-center gap-3 overflow-hidden ${isSidebarExpanded ? '' : 'justify-center'}`}>
                 <img src={user?.avatar || `https://ui-avatars.com/api/?name=${user?.name || 'Admin'}&background=2563eb&color=fff`} className="w-8 h-8 rounded-full border border-white/10 flex-shrink-0" alt="User" />
                 <AnimatePresence initial={false}>
                   {isSidebarExpanded && (
                     <motion.div
                       initial={{ opacity: 0 }}
                       animate={{ opacity: 1 }}
                       exit={{ opacity: 0 }}
                       transition={{ duration: 0.15 }}
                       className="flex-1 overflow-hidden"
                     >
                        <p className="text-[10px] font-black text-white truncate uppercase tracking-tight">{user?.name || 'Authorized Admin'}</p>
                        <p className="text-[8px] text-blue-100/40 truncate font-mono">{user?.role || 'SYSTEM_CORE'}</p>
                     </motion.div>
                   )}
                 </AnimatePresence>
                 <AnimatePresence initial={false}>
                   {isSidebarExpanded && (
                     <motion.button
                       initial={{ opacity: 0 }}
                       animate={{ opacity: 1 }}
                       exit={{ opacity: 0 }}
                       transition={{ duration: 0.15 }}
                       onClick={onLogout}
                       className="p-2 text-red-500/40 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all flex-shrink-0"
                     >
                        <LogOut className="w-4 h-4" />
                     </motion.button>
                   )}
                 </AnimatePresence>
              </div>
           </div>
        </div>
      </aside>

      {/* Main Workspace Frame */}
      <main className={`transition-all duration-500 ${isSidebarExpanded ? 'md:pl-72' : 'md:pl-20'}`}>
        {/* Strategic Topbar */}
        <header 
          className={`sticky top-0 z-40 transition-all duration-300 px-6 h-20 flex items-center justify-between ${scrolled ? 'bg-[#0a0b14]/80 backdrop-blur-3xl border-b border-white/5' : ''}`}
        >
          <div className="flex items-center gap-6">
             <div className="relative group hidden lg:block">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/10 group-focus-within:text-blue-500 transition-colors" />
                <input 
                  type="text" 
                  placeholder="Global Query Search..."
                  className="bg-white/5 border border-white/5 rounded-2xl py-3 pl-12 pr-6 text-xs text-white placeholder:text-white/10 w-64 focus:w-80 focus:ring-1 focus:ring-blue-500/30 transition-all outline-none"
                />
             </div>
             <div className="flex items-center gap-3 bg-white/5 border border-white/5 rounded-2xl px-4 py-2 invisible lg:visible">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]" />
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-emerald-400">System Link Secure</span>
             </div>
          </div>

          <div className="flex items-center gap-4">
             <div className="hidden lg:flex items-center gap-4 pr-6 border-r border-white/5">
                {[
                  { label: 'Latency', value: '14ms', icon: Cpu },
                  { label: 'Uptime', value: '99.99%', icon: Activity }
                ].map((stat, i) => (
                  <div key={i} className="flex flex-col items-end">
                     <span className="text-[8px] text-blue-100/20 font-black uppercase tracking-widest">{stat.label}</span>
                     <div className="flex items-center gap-1.5">
                        <stat.icon className="w-2.5 h-2.5 text-blue-400/40" />
                        <span className="text-[10px] font-mono text-blue-100/60 font-bold">{stat.value}</span>
                     </div>
                  </div>
                ))}
             </div>
             
             <div className="flex items-center gap-2">
                <NotificationBell />
                <button className="p-3 bg-white/5 hover:bg-blue-600/10 rounded-2xl border border-white/5 text-blue-100/40 hover:text-blue-400 transition-all">
                   <HelpCircle className="w-5 h-5" />
                </button>
                <div className="md:hidden">
                   <Button variant="ghost" className="p-2 text-white"><Menu className="w-6 h-6" /></Button>
                </div>
             </div>
          </div>
        </header>

        {/* Internal Slot Container */}
        <div className="relative min-h-[calc(100vh-80px)] overflow-x-hidden">
           {children}
        </div>
      </main>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
